import axios from "axios";

/**
 * Global HTTP request de-duplication for axios.
 *
 * Problem this solves:
 *   When the active account / channel changes (or the page is refreshed),
 *   several Redux slices update in quick succession. Dozens of independent
 *   data-fetching `useEffect`s across the app react to those changes and end
 *   up firing the *same* request more than once while the first one is still
 *   in flight. The result is the duplicate (double) API calls seen in the
 *   network panel.
 *
 * Strategy:
 *   While a request is in flight, any other request with an identical
 *   signature (method + url + params + body) reuses the same underlying
 *   network promise instead of issuing a new call. As soon as the request
 *   settles, its entry is cleared, so subsequent identical requests (e.g. an
 *   intentional manual refresh later on) are NOT affected and execute normally.
 *
 * This mirrors the request-deduplication that libraries like React Query and
 * SWR provide, applied transparently at the axios adapter layer so no feature
 * code has to change.
 *
 * Opt-out:
 *   Pass `dedupe: false` in any axios request config to bypass de-duplication
 *   for that specific call.
 */

// Tracks in-flight requests keyed by their computed signature.
const inFlightRequests = new Map();

/**
 * Deterministically serialize a value so equivalent payloads always produce
 * the same string regardless of key ordering.
 */
const stableStringify = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value !== "object") return String(value);

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  const keys = Object.keys(value).sort();
  return `{${keys
    .map((key) => `${key}:${stableStringify(value[key])}`)
    .join(",")}}`;
};

/**
 * Serialize a params/data segment of an axios config into a stable string.
 * Handles plain objects, arrays, strings and URLSearchParams (used widely in
 * this codebase via `createSearchParams`).
 */
const serializeSegment = (segment) => {
  if (segment === null || segment === undefined) return "";
  if (typeof segment === "string") return segment;

  if (typeof URLSearchParams !== "undefined" && segment instanceof URLSearchParams) {
    const entries = [...segment.entries()].sort(([a], [b]) =>
      a < b ? -1 : a > b ? 1 : 0
    );
    return entries.map(([key, val]) => `${key}=${val}`).join("&");
  }

  return stableStringify(segment);
};

/**
 * Some payloads cannot be safely serialized or compared (file uploads, binary
 * data, streams). De-duplicating those is risky, so we always let them pass
 * through untouched.
 */
const isUnserializableBody = (data) => {
  if (!data) return false;
  return (
    (typeof FormData !== "undefined" && data instanceof FormData) ||
    (typeof Blob !== "undefined" && data instanceof Blob) ||
    (typeof ArrayBuffer !== "undefined" && data instanceof ArrayBuffer) ||
    (typeof File !== "undefined" && data instanceof File) ||
    (typeof ReadableStream !== "undefined" && data instanceof ReadableStream)
  );
};

/**
 * Build a unique signature for an axios request config.
 */
const buildRequestKey = (config) => {
  const method = (config.method || "get").toLowerCase();
  const url = config.url || "";
  const baseURL = config.baseURL || "";
  const params = serializeSegment(config.params);
  const data = serializeSegment(config.data);
  return `${method}|${baseURL}${url}|${params}|${data}`;
};

/**
 * Install the de-duplication adapter on the global axios default.
 *
 * Must be called once, as early as possible during app bootstrap (before any
 * `axios.create()` instances issue requests) so created instances inherit the
 * wrapped adapter through `axios.defaults.adapter`.
 */
export const installRequestDeduplication = () => {
  if (axios.__requestDeduplicationInstalled) return;
  axios.__requestDeduplicationInstalled = true;

  // Resolve the platform default adapter (xhr/http/fetch) into a callable.
  const baseAdapter = axios.getAdapter(axios.defaults.adapter);

  axios.defaults.adapter = (config) => {
    // Respect explicit opt-out and skip non-serializable bodies.
    //
    // Requests carrying a cancel token / abort signal are also left untouched:
    // they own an independent lifecycle, and one caller aborting must never
    // tear down a shared request belonging to another caller.
    if (
      config.dedupe === false ||
      config.cancelToken ||
      config.signal ||
      isUnserializableBody(config.data)
    ) {
      return baseAdapter(config);
    }

    const key = buildRequestKey(config);

    let pending = inFlightRequests.get(key);
    if (!pending) {
      pending = baseAdapter(config).finally(() => {
        inFlightRequests.delete(key);
      });
      inFlightRequests.set(key, pending);
    }

    // Hand each caller its own response object so downstream response
    // transforms / interceptors operate independently and never collide.
    return pending.then((response) => ({ ...response }));
  };
};

export default installRequestDeduplication;
